import Vector3 from '../math/Vector3.js';

/**
 * Character - Core entity representing a character in the world
 * Used for both player characters and NPCs
 */
export default class Character {
  // Default dimensions for a character (slightly smaller than 1 to pass through 1-unit gaps)
  static DEFAULT_WIDTH = 0.9;
  static DEFAULT_HEIGHT = 2;
  static DEFAULT_DEPTH = 0.9;
  // Sort height for SAT depth sorting. The sorting AABB uses this (1-high), while
  // screen-space overlap detection uses the full visual height (DEFAULT_HEIGHT).
  // Smaller sort AABBs produce cleaner Y-axis separations in the SAT algorithm.
  static DEFAULT_SORT_HEIGHT = 1;

  /**
   * Create a new Character
   * @param {Object} options - Character options
   * @param {string} options.id - Unique identifier
   * @param {number} options.x - World X position
   * @param {number} options.y - World Y position (height)
   * @param {number} options.z - World Z position
   * @param {number} [options.width] - Collision box width
   * @param {number} [options.height] - Collision box height
   * @param {number} [options.depth] - Collision box depth
   */
  constructor(options = {}) {
    // Unique identifier
    this.id = options.id || `char_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // World position (y is vertical height)
    this.x = options.x || 0;
    this.y = options.y || 0;
    this.z = options.z || 0;

    // Collision box dimensions
    this.width = options.width || Character.DEFAULT_WIDTH;
    this.height = options.height || Character.DEFAULT_HEIGHT;
    this.depth = options.depth || Character.DEFAULT_DEPTH;
    // Sort height for SAT depth sorting (smaller AABB for cleaner Y-axis separations)
    this.sortHeight = options.sortHeight || Character.DEFAULT_SORT_HEIGHT;

    // Movement velocity
    this.velocity = new Vector3(0, 0, 0);

    // Direction character is facing (0-360 degrees, 0 = positive X / east)
    this.facingAngle = options.facingAngle || 0;

    // Ground state
    this.isGrounded = false;
    this.groundY = null;

    // Movement state for animation
    this.isMoving = false;
    this.isRunning = false;

    // References to components (set after construction)
    this.sprite = null;
    this.animator = null;
    this.controller = null;
    this.metadata = null;
    this.shadow = null;
    this.pathFollower = null;
    this.behavior = null; // NPCBehavior (NPCs only)

    // Whether this is an NPC (vs player)
    this.isNPC = false;

    // Whether this character is controlled by a remote peer (network-driven)
    this.isRemote = false;

    // Whether this NPC is controlled by the authority peer (skip local AI)
    this.isNetworkControlled = false;

    // Animation names (can be customized per character)
    this.animations = {
      idle: 'breathing-idle',
      walk: 'walking-8-frames',
      run: 'running-8-frames'
    };

    // Reference to world (set when added to world)
    this.world = null;
  }

  /**
   * Get the axis-aligned bounding box for collision detection
   * The character's position is at the center-bottom of the collision box
   * @returns {Object} AABB with min/max coordinates
   */
  getAABB() {
    const halfWidth = this.width / 2;
    const halfDepth = this.depth / 2;

    return {
      minX: this.x - halfWidth,
      minY: this.y,
      minZ: this.z - halfDepth,
      maxX: this.x + halfWidth,
      maxY: this.y + this.height,
      maxZ: this.z + halfDepth
    };
  }

  /**
   * Get AABB at a specific position (for collision prediction)
   * @param {number} x - X position
   * @param {number} y - Y position
   * @param {number} z - Z position
   * @returns {Object} AABB at the given position
   */
  getAABBAt(x, y, z) {
    const halfWidth = this.width / 2;
    const halfDepth = this.depth / 2;

    return {
      minX: x - halfWidth,
      minY: y,
      minZ: z - halfDepth,
      maxX: x + halfWidth,
      maxY: y + this.height,
      maxZ: z + halfDepth
    };
  }

  /**
   * Get a unique key for this character (used for spatial indexing)
   * @returns {string} Character key
   */
  getKey() {
    return this.id;
  }

  /**
   * Set the character's position
   * @param {number} x - World X position
   * @param {number} y - World Y position
   * @param {number} z - World Z position
   */
  setPosition(x, y, z) {
    this.x = x;
    this.y = y;
    this.z = z;
  }

  /**
   * Set the character's facing direction
   * @param {number} angle - Facing angle in degrees (0-360)
   */
  setFacing(angle) {
    this.facingAngle = ((angle % 360) + 360) % 360;
  }

  /**
   * Get the center position of the character (for sprite positioning)
   * @returns {Object} Center position {x, y, z}
   */
  getCenter() {
    return {
      x: this.x,
      y: this.y + this.height / 2,
      z: this.z
    };
  }

  /**
   * Get the position at the character's feet
   * @returns {Object} Feet position {x, y, z}
   */
  getFeetPosition() {
    return {
      x: this.x,
      y: this.y,
      z: this.z
    };
  }

  /**
   * Update the character state
   * Called each frame by the game loop
   * @param {number} deltaTime - Time since last frame in seconds
   * @param {Object} camera - Camera object for sprite updates
   */
  update(deltaTime, camera) {
    // Remote/network-controlled characters — only run animator + sprite + shadow
    if (this.isRemote || this.isNetworkControlled) {
      if (this.animator) this.animator.update(deltaTime);
      if (this.sprite) this.sprite.update(camera);
      if (this.shadow) this.shadow.update(camera);
      return;
    }

    // Update NPC behavior (picks destinations, manages state machine)
    // Must run before pathFollower so it can set new paths
    if (this.behavior) {
      this.behavior.update(deltaTime);
    }

    // Update path follower (handles automated pathfinding movement)
    // Must run before controller so controller can cancel path on input
    if (this.pathFollower && this.pathFollower.isFollowing) {
      this.pathFollower.update(deltaTime);
    }

    // Update controller (handles input and physics)
    if (this.controller) {
      this.controller.update(deltaTime);
    }

    // Update animator (handles animation state and frame timing)
    if (this.animator) {
      this.animator.update(deltaTime);
    }

    // Update sprite position and frame scaling.
    // This runs after animator.update() so frame positions are correctly scaled.
    // The World render pass also calls sprite.update() for depth sorting,
    // but we need it here too for frames where no render pass runs
    // (idle characters with static camera). Combat flickering is avoided
    // because combatManager.update() now runs BEFORE world.update().
    if (this.sprite) {
      this.sprite.update(camera);
    }

    // Update shadow projection
    if (this.shadow) {
      this.shadow.update(camera);
    }
  }

  /**
   * Set the movement state (for animation)
   * @param {boolean} moving - Whether the character is moving
   * @param {boolean} running - Whether the character is running
   * @param {boolean} force - Force animation change even if higher priority animation is playing
   */
  setMovementState(moving, running = false, force = false) {
    this.isMoving = moving;
    this.isRunning = running;

    // Trigger animation state change (names come from this.animations)
    if (this.animator) {
      if (!moving) {
        this.animator.setState(this.animations.idle, force);
      } else if (running) {
        this.animator.setState(this.animations.run, force);
      } else {
        this.animator.setState(this.animations.walk, force);
      }
    }
  }

  /**
   * Trigger an action animation (e.g., punch)
   * @param {string} action - Action name
   */
  triggerAction(action) {
    if (this.animator) {
      this.animator.setState(action);
    }
  }

  /**
   * Clean up resources when character is removed
   */
  destroy() {
    if (this.sprite) {
      this.sprite.destroy();
      this.sprite = null;
    }
    if (this.shadow) {
      this.shadow.destroy();
      this.shadow = null;
    }
    if (this.pathFollower) {
      this.pathFollower.stop();
      this.pathFollower = null;
    }
    this.behavior = null;
    this.controller = null;
    this.animator = null;
    this.world = null;
  }
}
